/**
 * Home: the organization's landing page. One still, centred column: the
 * greeting, a brief of what needs a hand now, four cards from the rest of the
 * platform, the lists under a tab bar, then suggested starts over the
 * composer, whose context pickers sit in its own row. A space has its own
 * page (components/spaces/SpacePage), not a Home view.
 *
 * Every row is real and scoped to the organization in the URL: chats from
 * the orchestration store, findings from the findings queue's own items,
 * policy review dates and regulatory deadlines.
 *
 * @module components/home/HomePage
 */
import { useMemo, useRef, useState } from "react";

import { useNowMinute } from "../../hooks/useNowMinute";
import { orgUrlSegment } from "../../orgRouting";
import { useAccountProfile } from "../../state/accountProfile";
import { useProjects, useThreadShells } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useOrgGovernance } from "../../state/governance";
import { useActiveOrgId } from "../../state/orgs";
import { usePolicies, useRegulatoryUpdates } from "../../state/policies";
import { NEW_CHAT_PLACEHOLDER } from "../chat/composerCopy";
import { useFindingItems } from "../regulation/finding/useFindingItems";
import { SidebarInset, SidebarTrigger } from "../ui/sidebar";
import { briefSentences } from "./homeBrief.logic";
import { HomeBrief } from "./HomeBrief";
import { HomeComposer, type HomeComposerApi } from "./HomeComposer";
import { groupOpenFindingsByDocument, homeFindingsStatus } from "./HomeFindings.logic";
import { greetingFor, greetingName, homeThreadState, selectComingUp, selectOrgThreads, selectWaitingChats } from "./homeLogic";
import { HomeStatCards } from "./HomeStatCards";
import { homeStatCards } from "./homeStatCards.logic";
import { HomeSuggestions } from "./HomeSuggestions";
import { HomeTabBarLists } from "./HomeTabBarLists";
import { useHomeGlance } from "./useHomeGlance";
import { useHomeSuggestions } from "./useHomeSuggestions";

export function HomePage() {
  const orgId = useActiveOrgId();
  const environmentId = usePrimaryEnvironmentId();
  const nowMinute = useNowMinute();
  const now = Date.parse(`${nowMinute}:00.000Z`) || Date.now();
  const { account } = useAccountProfile();
  const threads = useThreadShells();
  const projects = useProjects();
  const findings = useFindingItems(orgId);
  const governance = useOrgGovernance(orgId);
  const updates = useRegulatoryUpdates(orgId);
  const library = usePolicies(orgId).snapshot;
  const orgSegment = orgId ? orgUrlSegment(orgId) : null;
  const composer = useRef<HomeComposerApi | null>(null);
  const [composerHasText, setComposerHasText] = useState(false);

  const orgProject = useMemo(
    () =>
      projects.find((project) => project.id === orgId && project.environmentId === environmentId) ?? null,
    [environmentId, orgId, projects],
  );
  const orgThreads = useMemo(
    () => selectOrgThreads(threads, { orgId, environmentId }),
    [environmentId, orgId, threads],
  );
  const chat = {
    working: orgThreads.filter((thread) => homeThreadState(thread) === "working").length,
    waiting: selectWaitingChats(orgThreads).length,
  };

  const openItems = useMemo(() => findings.items.filter((item) => item.status === "open"), [findings.items]);
  const procedureIds = useMemo(
    () => new Set((library?.policies ?? []).filter((policy) => policy.documentKind === "procedure").map((policy) => policy.id)),
    [library],
  );
  const findingGroups = useMemo(() => groupOpenFindingsByDocument(openItems, procedureIds), [openItems, procedureIds]);
  const findingsStatus = homeFindingsStatus(findings);
  const comingUp = useMemo(
    () =>
      selectComingUp({
        policies: governance.snapshot?.policies ?? [],
        updates: updates.snapshot?.updates ?? [],
        now,
      }),
    [governance.snapshot, now, updates.snapshot],
  );
  const glance = useHomeGlance(openItems);
  const prepare = (prompt: string) => composer.current?.prefill(prompt);
  const suggestions = useHomeSuggestions(orgSegment && orgProject ? orgId : null, { findings: openItems, comingUp, glance, now });

  const firstName = account ? greetingName(account) : null;
  const greeting = greetingFor(new Date(now).getHours());

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <header className="flex h-11 shrink-0 items-center px-3 md:hidden">
        <SidebarTrigger aria-label="Open Home sidebar" />
      </header>
      {/* Still on any ordinary window; a window shorter than the page scrolls rather than clipping it. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain tabular-nums" data-home="">
        <div className="mx-auto my-auto flex max-h-[50rem] min-h-min w-full max-w-[calc(48rem+5rem)] flex-1 flex-col justify-center px-5 pt-3 pb-6 sm:px-10 md:pt-12">
          <h1 className="flex min-w-0 items-center gap-3 text-[26px] font-semibold leading-tight tracking-tight" data-home-headline="">
            <span>
              {greeting}
              {firstName ? `, ${firstName}` : ""}
            </span>
          </h1>
          {orgSegment ? (
            <div className="mt-2 flex min-h-0 flex-col" data-home-body="">
              <HomeBrief sentences={briefSentences({ glance, comingUp, now })} orgSegment={orgSegment} />
              <div className="mt-6">
                <HomeStatCards cards={homeStatCards({ chat, findings: openItems, findingsStatus, glance, now })} orgSegment={orgSegment} />
              </div>
              <HomeTabBarLists
                data={{
                  orgSegment,
                  now,
                  glance,
                  findings: openItems,
                  findingGroups,
                  findingsStatus,
                  onRetryFindings: findings.refresh,
                  comingUp,
                  onPrepare: prepare,
                }}
              />
            </div>
          ) : null}
          <div className="mt-6 shrink-0">
            {orgSegment && orgProject ? (
              <HomeSuggestions
                suggestions={suggestions}
                hidden={composerHasText}
                onPick={prepare}
              />
            ) : null}
            {orgProject ? (
              <HomeComposer
                apiRef={composer}
                placeholder={NEW_CHAT_PLACEHOLDER}
                project={{
                  id: orgProject.id,
                  environmentId: orgProject.environmentId,
                  defaultModelSelection: orgProject.defaultModelSelection,
                }}
                onHasTextChange={setComposerHasText}
              />
            ) : (
              <p className="rounded-[22px] border border-dashed border-border/70 px-5 py-6 text-center text-[14px] text-muted-foreground">
                This organization’s workspace is being prepared. Chat opens here when it is ready.
              </p>
            )}
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}
